import { api } from "./client";
import type { RoleCodeValue } from "../lib/rbac";
import { ApiClientError } from "./types";

export interface SubscriptionInfo {
  status: number;
  trialEnds: string | null;
  paidUntil: string | null;
  isActive: boolean;
  isTrial: boolean;
  isExpired: boolean;
  daysRemaining: number;
}

export interface UserProfile {
  userId: string;
  orgId: string;
  email: string;
  login: string;
  displayName: string;
  companyName?: string;
  whatsapp?: string;
  locale: string;
  status: number;
  roles: RoleCodeValue[];
  roleLabels: string[];
  lastLoginAt: string | null;
  subscription?: SubscriptionInfo;
}

export interface LoginRequest {
  login: string;
  password: string;
  orgId?: string;
}

export interface LoginResponse {
  ok: true;
  token: string;
  profile: UserProfile;
}

export interface TrialConfig {
  trialDays: number;
  registrationEnabled: boolean;
  requiresProvisionKey: boolean;
}

export interface RegisterRequest {
  companyName: string;
  ownerName?: string;
  email: string;
  whatsapp: string;
  password: string;
  confirmPassword: string;
  provisionKey?: string;
}

export interface PasswordResetRequestPayload {
  email: string;
  whatsappTail: string;
  channel: 0 | 1;
}

export async function login(
  credentials: LoginRequest,
): Promise<LoginResponse> {
  return api.post<LoginResponse>("/v1/auth/login", credentials, {
    skipAuth: true,
  });
}

export async function registerAccount(
  payload: RegisterRequest,
): Promise<LoginResponse> {
  return api.post<LoginResponse>("/v1/auth/register", payload, {
    skipAuth: true,
  });
}

export async function fetchTrialConfig(): Promise<TrialConfig> {
  const res = await api.get<{ ok: true; data: TrialConfig }>(
    "/v1/auth/trial-config",
    { skipAuth: true },
  );
  return res.data;
}

export async function requestPasswordReset(
  payload: PasswordResetRequestPayload,
): Promise<{ ok: true; message: string; devOtp?: string }> {
  return api.post("/v1/auth/password-reset/request", payload, {
    skipAuth: true,
  });
}

export async function confirmPasswordReset(payload: {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}): Promise<{ ok: true; message: string }> {
  return api.post("/v1/auth/password-reset/confirm", payload, {
    skipAuth: true,
  });
}

export async function fetchSession(): Promise<{ ok: true; profile: UserProfile }> {
  return api.get("/v1/auth/me");
}

export interface HealthResponse {
  ok: true;
  service: string;
  phase: number;
  time: string;
}

export async function fetchHealth(): Promise<HealthResponse> {
  return api.get<HealthResponse>("/health");
}

export function isSubscriptionExpiredError(err: unknown): err is ApiClientError {
  return (
    err instanceof ApiClientError &&
    (err.reason === "subscription_expired" || err.status === 402)
  );
}
